import { useEffect } from "react";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useOrderRealtime } from "@utils/store/realtime/useOrderRealtime";
import { isPracticeMode } from "@utils/practice/practiceMode";
import { isPayLinkMode } from "@utils/payLink";
import { openEventsSubscription } from "./eventsRealtime";
import { fetchCustomerRealtimeSession } from "./orderRealtimeApi";
import { parseEventFrame, parseOrderEvent, reconnectDelayMs } from "./orderRealtimeSession";
import type { CustomerRealtimeSession, OrderRealtimeEvent } from "./orderRealtimeSession";

/**
 * 訂單即時通知的連線生命週期（PageWrapper 呼叫一次）。
 *
 * 1. 向 app-api 要一張 10 分鐘的通行證（含頻道 /customer/{id} 與連線位址）。
 * 2. 帶通行證連 AppSync Events、訂閱自己的頻道；mirror 同步完訂單就推一個「該重抓了」的事件。
 * 3. 事件丟進 useOrderRealtime store，由列表／詳情頁各自決定要不要靜默重抓。
 *
 * 不做到期前續發（跟配送追蹤不同）：Events API 只在 connect / subscribe 時授權，
 * 連線建立後通行證過期也不會被斷；斷了就走重連，重連前本來就會換一張新的。
 *
 * 只在訂單頁連（active）：離開訂單頁就關，避免整個 LIFF 開著時一直掛著連線。
 * 練習站與付款連結頁不連：前者不能有任何真實後端流量，後者沒有客戶登入身分。
 *
 * 規格：lpg-customer-realtime docs/customer-realtime-spec.md 合約 C7。
 */
export function useOrderRealtimeConnection({ active }: { active: boolean }): void {
  const { cusJwtToken } = useCusAuth();

  useEffect(() => {
    // 用 getState() 而不是 hook：PageWrapper 不需要因為連線狀態或事件而重新 render
    const store = useOrderRealtime.getState();
    if (!active || !cusJwtToken || isPracticeMode() || isPayLinkMode()) {
      store.setStatus("idle");
      return;
    }

    let canceled = false;
    let sub: { close(): void } | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    /**
     * 這個 effect 生命週期內是否已經成功訂閱過。
     * 第二次以後的 subscribe_success 代表「斷線後重新接上」，中間的事件收不到，要請頁面補抓一次。
     */
    let hadSubscribed = false;
    /**
     * 每次 start() 遞增。通行證還在路上時分頁可能切背景又切回來，觸發第二次 start()；
     * 先發的那次回來時已經過時，不能再開一條連線（否則會有兩條連線同時訂閱）。
     */
    let generation = 0;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const closeSub = () => {
      sub?.close();
      sub = null;
    };

    const scheduleReconnect = () => {
      if (canceled || document.hidden) return;
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        // 計時器等待期間分頁可能切到背景；背景時交給 visibilitychange 處理，這裡不重連
        if (canceled || document.hidden) return;
        attempt += 1;
        void start();
      }, reconnectDelayMs(attempt));
    };

    const start = async () => {
      if (canceled) return;
      generation += 1;
      const myGeneration = generation;
      clearTimer();
      closeSub();
      useOrderRealtime.getState().setStatus("connecting");

      let session: CustomerRealtimeSession;
      try {
        session = await fetchCustomerRealtimeSession(cusJwtToken);
      } catch (error) {
        if (canceled || myGeneration !== generation) return;
        // 背景重試不跳 toast（後端沒部署或密鑰沒建時會一直失敗，會變成連環彈窗）；30 秒輪詢仍在保底
        console.warn("[orderRealtime] 取得即時通知通行證失敗", error);
        useOrderRealtime.getState().setStatus("disconnected");
        scheduleReconnect();
        return;
      }
      // await 期間分頁可能已切到背景、或已有更新的 start()：這張通行證不再算數
      if (canceled || myGeneration !== generation || document.hidden) return;

      /**
       * 舊連線關掉之後，瀏覽器緩衝中的訊息仍可能觸發它的 callback；
       * 只認目前這條，否則舊連線的 onAuthError 會把新連線關掉。
       */
      let current: { close(): void } | null = null;
      const isStale = () => canceled || sub !== current;
      current = openEventsSubscription({
        wssUrl: session.realtime.wssUrl,
        httpHost: session.realtime.httpHost,
        token: session.token,
        channel: session.channel,
        // 退避歸零要等訂閱真的被接受；只看 connection_ack 的話，訂閱被拒時會固定以第一段間隔無限重試
        onSubscribed: () => {
          if (isStale()) return;
          useOrderRealtime.getState().setStatus("subscribed");
          attempt = 0;
          if (hadSubscribed) useOrderRealtime.getState().bumpResume();
          hadSubscribed = true;
        },
        onEvent: (raw: unknown) => {
          if (isStale()) return;
          const { pushEvent } = useOrderRealtime.getState();
          parseEventFrame(raw)
            .map(parseOrderEvent)
            .forEach((e: OrderRealtimeEvent | null) => {
              if (e) pushEvent(e);
            });
        },
        onAuthError: () => {
          if (isStale()) return;
          // 通行證被拒：關掉這條，重連時會換新通行證
          closeSub();
          useOrderRealtime.getState().setStatus("disconnected");
          scheduleReconnect();
        },
        onClose: () => {
          if (isStale()) return;
          sub = null;
          useOrderRealtime.getState().setStatus("disconnected");
          scheduleReconnect();
        },
      });
      sub = current;
    };

    /**
     * LINE 切到背景時 WebSocket 常被系統默默斷掉，斷線期間的事件就漏了。
     * 所以背景時乾脆自己關掉；回到前景先請頁面靜默補抓一次（補上漏掉的），再重新連線。
     */
    const onVisibilityChange = () => {
      if (canceled) return;
      if (document.hidden) {
        generation += 1; // 讓還在路上的 start() 回來後作廢
        clearTimer();
        closeSub();
        useOrderRealtime.getState().setStatus("disconnected");
        return;
      }
      useOrderRealtime.getState().bumpResume();
      attempt = 0;
      void start();
    };

    // 一開始就在背景（例如 LIFF 在背景被開起來）：等 visibilitychange 再連
    if (!document.hidden) void start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      canceled = true;
      clearTimer();
      closeSub();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      useOrderRealtime.getState().setStatus("idle");
    };
  }, [active, cusJwtToken]);
}
